<template>
  <div class="space-y-10">
    <!-- ═══ HERO SECTION ═══ -->
    <header class="relative text-center space-y-6 py-8">
      <!-- Decorative lotus glow behind hero -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] pointer-events-none"
           style="background: radial-gradient(ellipse, rgba(232, 139, 199, 0.08), transparent 70%);"></div>
      
      <!-- Lotus ornament -->
      <div class="flex justify-center">
        <div class="relative">
          <svg class="w-12 h-12 text-nirvana-lotus/50 animate-float" viewBox="0 0 48 48" fill="currentColor">
            <path d="M24 4C24 4 16 12 16 20C16 24 19 27 24 28C29 27 32 24 32 20C32 12 24 4 24 4Z"/>
            <path d="M24 28C18 28 12 22 10 16C8 22 10 30 16 34C19 36 22 36 24 36C26 36 29 36 32 34C38 30 40 22 38 16C36 22 30 28 24 28Z" opacity="0.5"/>
            <path d="M24 36C20 36 14 32 12 28C11 32 13 38 18 40C20 41 22 41 24 42C26 41 28 41 30 40C35 38 37 32 36 28C34 32 28 36 24 36Z" opacity="0.3"/>
          </svg>
          <div class="absolute inset-0 blur-xl bg-nirvana-lotus/20 animate-glow-pulse"></div>
        </div>
      </div>

      <h1 class="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-nirvana-cyan via-nirvana-white to-nirvana-lotus bg-clip-text text-transparent leading-tight">
        อวยพรวันเกิดให้น้องเนอร์กัน~
      </h1>

      <p class="text-nirvana-silver/60 max-w-lg mx-auto text-base leading-relaxed">
        แบ่งปันคำอวยพรวันเกิดให้น้องตุ๊กตาสุดน่ารัก<br>
        <span class="text-nirvana-lotus/70">เนอร์วาน่าของพวกเราเหล่าแฮนด์เลอร์!</span>
      </p>

      <div class="flex flex-col items-center gap-3">
        <div class="flex flex-wrap justify-center gap-3 sm:gap-4">
          <button disabled class="lotus-button text-base opacity-50 cursor-not-allowed">
            เขียนคำอวยพร
          </button>
          <router-link to="/wish/0" class="lotus-button text-base" style="background: rgba(140, 235, 255, 0.15); border-color: rgba(140, 235, 255, 0.3); box-shadow: 0 4px 15px rgba(140, 235, 255, 0.1);">
            อ่านคำอวยพร
          </router-link>
        </div>
        <p class="text-sm text-nirvana-silver/30 mt-2">
          มีคำอวยพรทั้งหมด <span class="text-nirvana-cyan/60 font-medium">{{ displayTotalWishes }}</span> คำอวยพร
        </p>
      </div>

      <!-- Ornate horizontal divider -->
      <div class="max-w-xs mx-auto ornate-divider"></div>
    </header>

    <!-- ═══ WISHES LIST ═══ -->
    <div
      ref="containerRef"
      class="scroll-container space-y-5 px-1"
    >
      <!-- Developer Wish Card always appears first -->
      <DevWishCard v-if="devWish" :wish="devWish" />

      <template v-if="wishes.length">
        <WishCard
          v-for="wish in wishes"
          :key="wish.id"
          :wish="wish"
        />
      </template>

      <LoadingSpinner v-if="loading" />

      <div
        v-if="!loading && !wishes.length && !devWish"
        class="crystal-card text-center py-12"
      >
        <svg class="w-16 h-16 mx-auto text-nirvana-purple/30 mb-4" viewBox="0 0 48 48" fill="currentColor">
          <path d="M24 4C24 4 16 12 16 20C16 24 19 27 24 28C29 27 32 24 32 20C32 12 24 4 24 4Z"/>
          <path d="M24 28C18 28 12 22 10 16C8 22 10 30 16 34C19 36 22 36 24 36C26 36 29 36 32 34C38 30 40 22 38 16C36 22 30 28 24 28Z" opacity="0.5"/>
        </svg>
        <p class="text-nirvana-silver/40">ยังไม่มีใครอวยพรน้องเลย~ ทำไมไม่มาเป็นคนแรกล่ะ!</p>
      </div>

      <p v-if="error" class="text-center text-red-400/80 crystal-card py-4">
        {{ error }}
      </p>
    </div>

    <!-- Tutorial Popup -->
    <TutorialPopup />
  </div>
</template>

<script setup>
import { WishCard, LoadingSpinner, TutorialPopup } from '@/components'
import DevWishCard from '@/components/DevWishCard.vue'
import { devWishData } from '@/config/devWish'
import { useWishes, useInfiniteScroll } from '@/composables'
import { ref, onMounted, computed, onUnmounted } from 'vue'

const {
  wishes,
  loading,
  error,
  hasMore,
  loadWishes,
  totalCount,
  refreshWishes
} = useWishes()

const { containerRef } = useInfiniteScroll(loadWishes, {
  threshold: 100
})

const devWish = ref(null)

const totalWishes = computed(() => {
  if (totalCount && totalCount.value !== undefined) {
    return totalCount.value + (devWish.value ? 1 : 0);
  }
  return wishes.value.length + (devWish.value ? 1 : 0);
})

const displayTotalWishes = computed(() => {
  const count = totalWishes.value;
  return count > 0 ? count : devWish.value ? 1 : 0;
})

let refreshInterval;

onMounted(async () => {
  try {
    devWish.value = devWishData
  } catch (e) {
    console.error('Failed to load developer wish:', e)
  }

  refreshInterval = setInterval(() => {
    if (refreshWishes) {
      refreshWishes();
    }
  }, 5000);
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
})
</script>

<style scoped>
.scroll-container {
  max-height: 800px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
